import * as PIXI from 'pixi.js';
import { Colors } from '~/constants';
import { CavernsStore, GameStateStore, HeroBuildStore } from '~/flux/stores';
import text from '~/text';
import CanvasTools from '~/view/CanvasTools';
import { HeroLevelView } from '~/view/components/common/canvas';
import { ObjectPool } from '~/Tools';
import { findHeroBuildById } from '~/helpers/findHeroBuildById';

// Object pool for loss bar Graphics
const _lossBarPool = new ObjectPool(
  // create
  () => new PIXI.Graphics(),
  // reset
  (bar) => {
    bar.clear();
    bar.parent?.removeChild(bar);
    bar.x = 0;
    bar.y = 0;
    bar.scale.x = 1;
    bar.scale.y = 1;
    TweenMax.killTweensOf(bar);
    TweenMax.killTweensOf(bar.scale);
  },
  // initial size
  30
);

const CavernsUnitDetailsView = function (unit_state, loadout_member) {
  PIXI.Container.call(this);

  // handle missing unit_state (i.e. not found in battleState.allUnits, i.e. dead)
  if (!unit_state) {
    unit_state = {
      hp: 0,
      max_hp: 0,
      mp: 0,
      max_mp: 0,
      ap: 0,
      max_ap: 0,
    };
  }

  const hero_handle = loadout_member.hero_handle || loadout_member.handle;
  var _unit = (this.unit_state = unit_state);
  var _namePlate;
  var _hpField, _apField, _mpField;
  let _hpLossBar, _mpLossBar, _apLossBar;
  var disposed = false;

  // Track last values for loss bars
  let _last_hp = unit_state.hp;
  let _last_mp = unit_state.mp;
  let _last_ap = unit_state.ap;
  let _hp_bar_tween, _mp_bar_tween, _ap_bar_tween;

  this.dispose = () => {
    // Kill any active loss bar tweens
    _hp_bar_tween?.kill();
    _mp_bar_tween?.kill();
    _ap_bar_tween?.kill();
    _hp_bar_tween = null;
    _mp_bar_tween = null;
    _ap_bar_tween = null;

    // Return loss bars to pool
    if (_hpLossBar) {
      _lossBarPool.put(_hpLossBar);
      _hpLossBar = null;
    }
    if (_mpLossBar) {
      _lossBarPool.put(_mpLossBar);
      _mpLossBar = null;
    }
    if (_apLossBar) {
      _lossBarPool.put(_apLossBar);
      _apLossBar = null;
    }

    destroyNamePlate();
    destroyStatBars();

    DT_CANVAS_GLOBALS.poolSafeDestroy(this, { children: true });

    _namePlate = null;
    _unit = null;
    _hpField = null;
    _apField = null;
    _mpField = null;

    disposed = true;
  };

  const destroyNamePlate = () => {
    if (_namePlate) {
      _namePlate.hero_level_view?.dispose();
      _namePlate.hero_build_name = null;
      _namePlate.txt = null;
      DT_CANVAS_GLOBALS.poolSafeDestroy(_namePlate, { children: true });
      _namePlate = null;
    }
  };

  const destroyStatBars = () => {
    if (_hpField) {
      DT_CANVAS_GLOBALS.poolSafeDestroy(_hpField, { children: true });
      _hpField = null;
    }

    if (_mpField) {
      DT_CANVAS_GLOBALS.poolSafeDestroy(_mpField, { children: true });
      _mpField = null;
    }

    if (_apField) {
      DT_CANVAS_GLOBALS.poolSafeDestroy(_apField, { children: true });
      _apField = null;
    }
  };

  const makeNamePlate = () => {
    destroyNamePlate();

    _namePlate = new PIXI.Sprite();
    _namePlate.texture = PIXI.utils.TextureCache['armory/class_label_default.png'];
    _namePlate.tint = Colors[hero_handle];
    _namePlate.txt = new PIXI.Text(text(`heroes.${hero_handle}.name`).toUpperCase(), {
      fontFamily: 'Courier New',
      fontSize: 22,
      fontWeight: 'bold',
      fill: 0xffffff,
      dropShadow: true,
      dropShadowDistance: 1,
      dropShadowColor: 0x000000,
    });
    _namePlate.txt.x = Math.round(_namePlate.width / 2 - _namePlate.txt.width / 2);
    _namePlate.txt.y = Math.round(_namePlate.height * 0.65 - _namePlate.txt.height / 2);
    _namePlate.addChild(_namePlate.txt);
    _namePlate.height = Math.round(window.innerHeight * 0.048);
    _namePlate.scale.x = _namePlate.scale.y;
    this.addChild(_namePlate);

    const hlv = (_namePlate.hero_level_view = new HeroLevelView({
      roster_hero: GameStateStore.getAll().gameState.hero_roster[hero_handle],
      scale_mod: 1.3,
    }));
    hlv.scale.x = hlv.scale.y = 0.5;
    hlv.x = Math.round(_namePlate.width / 2 / _namePlate.scale.x - hlv.width / 2);
    hlv.y = Math.round(_namePlate.height / _namePlate.scale.y - hlv.height * 0.25);
    _namePlate.addChild(hlv);

    const heroBuild = findHeroBuildById(
      HeroBuildStore.getAll().hero_builds[hero_handle],
      loadout_member.hero_build_id
    );
    _namePlate.hero_build_name = new PIXI.Text(
      heroBuild?.build_display_name || text('heroes.basic'),
      {
        fontFamily: 'Courier New',
        fontSize: 14,
        fill: Colors[hero_handle],
        dropShadow: true,
        dropShadowDistance: 1,
        dropShadowColor: 0xffffff,
      }
    );
    _namePlate.hero_build_name.x = Math.round(
      _namePlate.width / 2 / _namePlate.scale.x - _namePlate.hero_build_name.width / 2
    );
    _namePlate.hero_build_name.y = Math.round(-_namePlate.hero_build_name.height * 0.4);
    _namePlate.addChild(_namePlate.hero_build_name);
  };
  makeNamePlate();

  const BAR_WIDTH = window.innerWidth * 0.09;
  const BAR_HEIGHT = window.innerHeight * 0.023;
  const labelStyle = {
    fontFamily: 'Courier New',
    fontSize: CanvasTools.dynamicFontSizeString(13),
    fontWeight: 'bold',
    fill: 0xffffff,
    dropShadow: true,
    dropShadowColor: 0x000000,
    dropShadowDistance: 1,
  };

  const makeStatBars = () => {
    destroyStatBars();

    // make HP Bar
    _hpField = new PIXI.Container();
    var gfx = new PIXI.Graphics();
    gfx.beginFill(Colors.WHITE_TEAM_DARK);
    gfx.drawRect(0, 0, BAR_WIDTH, BAR_HEIGHT);
    gfx.endFill();
    _hpField.bgGfx = gfx;
    _hpField.addChild(gfx);

    var label = new PIXI.Text(text('game.HP'), labelStyle);
    label.x = Math.round(-label.width - DT_CANVAS_GLOBALS.spacing * 0.5);
    label.y = Math.round(_hpField.y + _hpField.height / 2 - label.height / 2);
    _hpField.label = label;
    _hpField.addChild(label);

    var value = _unit.hp;
    var maxValue = _unit.max_hp;
    var fullSprite = new PIXI.Sprite(PIXI.Texture.WHITE);
    fullSprite.tint = Colors.WHITE_TEAM;
    fullSprite.width = Math.max(1, BAR_WIDTH * (maxValue > 0 ? value / maxValue : 0));
    fullSprite.height = BAR_HEIGHT;
    _hpField.fullSprite = fullSprite;
    _hpField.addChild(fullSprite);

    var valueText = new PIXI.Text(
      CanvasTools.getBriefString(value) + ' / ' + CanvasTools.getBriefString(maxValue),
      labelStyle
    );
    valueText.x = Math.round(BAR_WIDTH / 2 - valueText.width / 2);
    valueText.y = Math.round(BAR_HEIGHT / 2 - valueText.height / 2);
    _hpField.addChild(valueText);

    _hpField.x = Math.round(_namePlate.x + _namePlate.width + DT_CANVAS_GLOBALS.spacing * 3.5);
    _hpField.y = _namePlate.y - DT_CANVAS_GLOBALS.spacing * 0.9;
    _hpField.valueText = valueText;
    this.addChild(_hpField);

    // make MP Bar
    _mpField = new PIXI.Container();
    gfx = new PIXI.Graphics();
    gfx.beginFill(Colors.MP_PURPLE_DARK);
    gfx.drawRect(0, 0, BAR_WIDTH, BAR_HEIGHT);
    gfx.endFill();
    _mpField.bgGfx = gfx;
    _mpField.addChild(gfx);

    label = new PIXI.Text(text('game.MP'), labelStyle);
    label.x = Math.round(-label.width - DT_CANVAS_GLOBALS.spacing * 0.5);
    label.y = Math.round(_mpField.y + _mpField.height / 2 - label.height / 2);
    _mpField.label = label;
    _mpField.addChild(label);

    value = _unit.mp;
    maxValue = _unit.max_mp;
    fullSprite = new PIXI.Sprite(PIXI.Texture.WHITE);
    fullSprite.tint = Colors.MP_PURPLE;
    fullSprite.width = Math.max(1, BAR_WIDTH * (maxValue > 0 ? value / maxValue : 0));
    fullSprite.height = BAR_HEIGHT;
    _mpField.fullSprite = fullSprite;
    _mpField.addChild(fullSprite);

    valueText = new PIXI.Text(
      CanvasTools?.getBriefString(value) + ' / ' + CanvasTools?.getBriefString(maxValue),
      labelStyle
    );
    valueText.x = Math.round(BAR_WIDTH / 2 - valueText.width / 2);
    valueText.y = Math.round(BAR_HEIGHT / 2 - valueText.height / 2);
    _mpField.valueText = valueText;
    _mpField.addChild(valueText);

    _mpField.x = Math.round(_hpField.x);
    _mpField.y = Math.round(_hpField.y + _hpField.height + 1);
    this.addChild(_mpField);

    // make AP Bar
    _apField = new PIXI.Container();
    gfx = new PIXI.Graphics();
    gfx.beginFill(Colors.AP_ORANGE_DARK);
    gfx.drawRect(0, 0, BAR_WIDTH, BAR_HEIGHT);
    gfx.endFill();
    _apField.bgGfx = gfx;
    _apField.addChild(gfx);

    label = new PIXI.Text(text('game.AP'), labelStyle);
    label.x = Math.round(-label.width - DT_CANVAS_GLOBALS.spacing * 0.5);
    label.y = Math.round(_apField.y + _apField.height / 2 - label.height / 2);
    _apField.label = label;
    _apField.addChild(label);

    value = _unit.ap;
    maxValue = _unit.max_ap;
    fullSprite = new PIXI.Sprite(PIXI.Texture.WHITE);
    fullSprite.tint = Colors.AP_ORANGE;
    fullSprite.width = Math.max(1, BAR_WIDTH * (maxValue > 0 ? value / maxValue : 0));
    fullSprite.height = BAR_HEIGHT;
    _apField.fullSprite = fullSprite;
    _apField.addChild(fullSprite);

    valueText = new PIXI.Text(
      CanvasTools?.getBriefString(value) + ' / ' + CanvasTools?.getBriefString(maxValue),
      labelStyle
    );
    valueText.x = Math.round(BAR_WIDTH / 2 - valueText.width / 2);
    valueText.y = Math.round(BAR_HEIGHT / 2 - valueText.height / 2);
    _apField.valueText = valueText;
    _apField.addChild(valueText);

    _apField.x = Math.round(_mpField.x);
    _apField.y = Math.round(_mpField.y + _mpField.height + 1);
    this.addChild(_apField);
  };
  makeStatBars();

  this.updateStats = () => {
    if (disposed) return;
    if (!_unit) return;
    if (!_hpField) return;
    if (!_mpField) return;
    if (!_apField) return;

    const { battleState } = CavernsStore.getAll();
    if (!battleState) return;
    _unit = battleState.allUnits[_unit.uid];
    if (!_unit) return;

    // Update HP
    var value = Math.max(0, _unit.hp);
    var maxValue = _unit.max_hp;
    _hpField.fullSprite.width = Math.max(1, BAR_WIDTH * (maxValue > 0 ? value / maxValue : 0));
    _hpField.valueText.text =
      CanvasTools?.getBriefString(value) + ' / ' + CanvasTools?.getBriefString(maxValue);
    _hpField.valueText.x = Math.round(BAR_WIDTH / 2 - _hpField.valueText.width / 2);

    // HP loss bar
    if (value < _last_hp) {
      const hpLeft = BAR_WIDTH * (maxValue > 0 ? Math.max(value, 0.1) / maxValue : 0);
      if (hpLeft < BAR_WIDTH) {
        const lossWidth = BAR_WIDTH * (maxValue > 0 ? (_last_hp - value) / maxValue : 0);

        if (_hpLossBar) {
          _lossBarPool.put(_hpLossBar);
        }
        _hpLossBar = _lossBarPool.get();
        _hpLossBar.beginFill(0xffffff, 0.2);
        _hpLossBar.drawRect(0, 0, lossWidth, BAR_HEIGHT);
        _hpLossBar.endFill();
        _hpLossBar.x = hpLeft;
        _hpField.addChild(_hpLossBar);

        _hp_bar_tween?.kill();
        _hp_bar_tween = TweenMax.to(_hpLossBar.scale, 0.3, {
          x: 0,
          onComplete: _lossBarPool.put,
          onCompleteParams: [_hpLossBar],
        });
      }
    }

    // Update MP
    value = Math.max(0, _unit.mp);
    maxValue = _unit.max_mp;
    _mpField.fullSprite.width = Math.max(1, BAR_WIDTH * (maxValue > 0 ? value / maxValue : 0));
    _mpField.valueText.text =
      CanvasTools?.getBriefString(value) + ' / ' + CanvasTools?.getBriefString(maxValue);
    _mpField.valueText.x = Math.round(BAR_WIDTH / 2 - _mpField.valueText.width / 2);

    // MP loss bar
    if (value < _last_mp) {
      const mpLeft = BAR_WIDTH * (maxValue > 0 ? Math.max(value, 0.1) / maxValue : 0);
      if (mpLeft < BAR_WIDTH) {
        const lossWidth = BAR_WIDTH * (maxValue > 0 ? (_last_mp - value) / maxValue : 0);

        if (_mpLossBar) {
          _lossBarPool.put(_mpLossBar);
        }
        _mpLossBar = _lossBarPool.get();
        _mpLossBar.beginFill(0xffffff, 0.2);
        _mpLossBar.drawRect(0, 0, lossWidth, BAR_HEIGHT);
        _mpLossBar.endFill();
        _mpLossBar.x = mpLeft;
        _mpField.addChild(_mpLossBar);

        _mp_bar_tween?.kill();
        _mp_bar_tween = TweenMax.to(_mpLossBar.scale, 0.3, {
          x: 0,
          onComplete: _lossBarPool.put,
          onCompleteParams: [_mpLossBar],
        });
      }
    }

    // Update AP
    value = Math.max(0, _unit.ap);
    maxValue = _unit.max_ap;
    _apField.fullSprite.width = Math.max(1, BAR_WIDTH * (maxValue > 0 ? value / maxValue : 0));
    _apField.valueText.text =
      CanvasTools?.getBriefString(value) + ' / ' + CanvasTools?.getBriefString(maxValue);
    _apField.valueText.x = Math.round(BAR_WIDTH / 2 - _apField.valueText.width / 2);

    // AP loss bar
    if (value < _last_ap) {
      const apLeft = BAR_WIDTH * (maxValue > 0 ? Math.max(value, 0.1) / maxValue : 0);
      if (apLeft < BAR_WIDTH) {
        const lossWidth = BAR_WIDTH * (maxValue > 0 ? (_last_ap - value) / maxValue : 0);

        if (_apLossBar) {
          _lossBarPool.put(_apLossBar);
        }
        _apLossBar = _lossBarPool.get();
        _apLossBar.beginFill(0xffffff, 0.2);
        _apLossBar.drawRect(0, 0, lossWidth, BAR_HEIGHT);
        _apLossBar.endFill();
        _apLossBar.x = apLeft;
        _apField.addChild(_apLossBar);

        _ap_bar_tween?.kill();
        _ap_bar_tween = TweenMax.to(_apLossBar.scale, 0.3, {
          x: 0,
          onComplete: _lossBarPool.put,
          onCompleteParams: [_apLossBar],
        });
      }
    }

    // Update tracked values
    _last_hp = _unit.hp;
    _last_mp = _unit.mp;
    _last_ap = _unit.ap;
  };

  this.onUnitDeath = () => {
    _hpField.fullSprite.scale.x = 0;
    _mpField.fullSprite.scale.x = 0;
    _apField.fullSprite.scale.x = 0;

    _hpField.valueText.text = 'xxx';
    _mpField.valueText.text = 'xxx';
    _apField.valueText.text = 'xxx';

    _hpField.valueText.x = Math.round(BAR_WIDTH / 2 - _hpField.valueText.width / 2);
    _mpField.valueText.x = Math.round(BAR_WIDTH / 2 - _mpField.valueText.width / 2);
    _apField.valueText.x = Math.round(BAR_WIDTH / 2 - _apField.valueText.width / 2);

    _namePlate.tint = 0x999999;
    _namePlate.hero_level_view.levelView.tint = 0x999999;
    this.alpha = 0.35;
  };
  unit_state.max_hp === 0 && this.onUnitDeath();
};
CavernsUnitDetailsView.prototype = Object.create(PIXI.Container.prototype);
CavernsUnitDetailsView.prototype.constructor = CavernsUnitDetailsView;
export default CavernsUnitDetailsView;
